// ============================================================
// CLUBHUB – Zusagen auswerten
// ============================================================
// Was aus den Antworten eines Vorkommens folgt: Zaehler, wer bei
// einer Hoechstzahl dabei ist und wer auf der Warteliste steht, und
// bis wann geantwortet werden kann.
//
// WARTELISTE ohne eigenes Feld: wer zuerst zusagt, ist dabei. Die
// Zusagen werden nach `respondedAt` geordnet; die ersten
// `maxParticipants` sind bestaetigt, der Rest wartet in dieser
// Reihenfolge. Sagt jemand ab, rueckt die erste Person nach.
//
// FRIST: ohne Angabe bis Terminbeginn (ClubDesk-Vorgabe). Bei einer
// Serie gilt die Frist als Abstand zum Beginn — „Frist 2 Stunden
// vorher“ fuer jedes Training, nicht ein fester Tag fuer alle.
// ============================================================
import type { AntwortView, TerminView, VorkommenView, ZusageZaehler } from "./views";
import { naivZuUtc, utcZuNaiv } from "./tag";

export type Auswertung = {
  zaehler: ZusageZaehler;
  /** Bestaetigte Zusagen (bei Hoechstzahl die ersten n). */
  dabei: Set<string>;
  /** Kontakte auf der Warteliste, in Reihenfolge. */
  warteliste: string[];
  /** Antwort je Kontakt (eingeladene ohne Antwort: PENDING). */
  jePerson: Map<string, AntwortView | null>;
};

export function zusagenAuswerten(antworten: AntwortView[], eingeladene: string[], max: number | null): Auswertung {
  const jePerson = new Map<string, AntwortView | null>();
  for (const id of eingeladene) jePerson.set(id, null);
  for (const a of antworten) jePerson.set(a.contactId, a);

  const zusagen = [...jePerson.values()]
    .filter((a): a is AntwortView => a?.status === "YES")
    .sort((a, b) => (a.respondedAt ?? "").localeCompare(b.respondedAt ?? ""));
  const grenze = max ?? Number.POSITIVE_INFINITY;
  const dabei = new Set(zusagen.slice(0, grenze).map((a) => a.contactId));
  const warteliste = zusagen.slice(grenze).map((a) => a.contactId);

  const zaehler: ZusageZaehler = { yes: dabei.size, no: 0, maybe: 0, pending: 0, warteliste: warteliste.length, eingeladen: jePerson.size };
  for (const a of jePerson.values()) {
    if (!a || a.status === "PENDING") zaehler.pending++;
    else if (a.status === "NO") zaehler.no++;
    else if (a.status === "MAYBE") zaehler.maybe++;
  }
  return { zaehler, dabei, warteliste, jePerson };
}

/** Bis wann geantwortet werden kann. */
export function antwortFrist(t: TerminView, v: Pick<VorkommenView, "startsAt" | "occurrence">): string {
  if (!t.rsvpDeadline) return v.startsAt;
  if (!t.rrule || !v.occurrence) return t.rsvpDeadline;
  const abstand = naivZuUtc(t.startsAt).getTime() - naivZuUtc(t.rsvpDeadline).getTime();
  return utcZuNaiv(new Date(naivZuUtc(v.startsAt).getTime() - abstand));
}

/** Quote in ganzen Prozent, ohne Nenner „–“. */
export function quote(teil: number, ganz: number): string {
  if (ganz === 0) return "–";
  return `${Math.round((teil / ganz) * 100)} %`;
}
